import { makeStyles, shorthands } from "@fluentui/react-components";
import { designTokens } from "./customTheme";

export const useSharedButtonStyles = makeStyles({
  primaryButton: {
    fontFamily: designTokens.fontFamily,
    backgroundColor: designTokens.accent[700],
    color: designTokens.neutral.white,
    ...shorthands.border("none"),
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
    height: "36px",
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    cursor: "pointer",
    transition: `background ${designTokens.transition.fast}, box-shadow ${designTokens.transition.fast}`,
    lineHeight: designTokens.lineHeight.normal,
    "& svg": {
      width: designTokens.iconSize.sm,
      height: designTokens.iconSize.sm,
    },
    ":hover:not(:disabled)": {
      backgroundColor: designTokens.accent[700],
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
      color: designTokens.neutral.white,
    },
    ":active:not(:disabled)": {
      backgroundColor: designTokens.accent[700],
      boxShadow: "none",
      color: designTokens.neutral.white,
    },
    ":disabled": {
      backgroundColor: designTokens.neutral[300],
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },

  secondaryButton: {
    fontFamily: designTokens.fontFamily,
    backgroundColor: designTokens.neutral.white,
    color: designTokens.neutral[700],
    ...shorthands.border("1px", "solid", designTokens.neutral[300]),
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
    height: "36px",
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    lineHeight: designTokens.lineHeight.normal,
    "& svg": {
      width: designTokens.iconSize.sm,
      height: designTokens.iconSize.sm,
    },
    ":hover:not(:disabled)": {
      backgroundColor: designTokens.neutral[200],
      ...shorthands.borderColor(designTokens.neutral[400]),
    },
    ":active:not(:disabled)": {
      backgroundColor: designTokens.neutral[300],
    },
    ":disabled": {
      backgroundColor: designTokens.neutral[100],
      color: designTokens.neutral[400],
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },

  smallButton: {
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
    fontSize: designTokens.fontSize.xs,
    borderRadius: designTokens.borderRadius.md,
  },

  largeButton: {
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[8]}`,
    fontSize: designTokens.fontSize.base,
    borderRadius: designTokens.borderRadius.xl,
  },

  dangerButton: {
    fontFamily: designTokens.fontFamily,
    backgroundColor: designTokens.semantic.error,
    color: designTokens.neutral.white,
    ...shorthands.border("none"),
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    cursor: "pointer",
    transition: `background ${designTokens.transition.fast}`,
    lineHeight: designTokens.lineHeight.normal,
    ":hover": {
      backgroundColor: designTokens.semantic.errorHover,
    },
    ":active": {
      backgroundColor: designTokens.semantic.errorActive,
    },
    ":disabled": {
      backgroundColor: designTokens.neutral[300],
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },

  outlineButton: {
    fontFamily: designTokens.fontFamily,
    backgroundColor: "transparent",
    color: designTokens.accent[600],
    ...shorthands.border("1px", "solid", designTokens.accent[500]),
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    lineHeight: designTokens.lineHeight.normal,
    ":hover": {
      backgroundColor: designTokens.accent[50],
      ...shorthands.borderColor(designTokens.accent[600]),
    },
    ":active": {
      backgroundColor: designTokens.accent[100],
    },
    ":disabled": {
      color: designTokens.neutral[400],
      ...shorthands.borderColor(designTokens.neutral[300]),
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },

  ghostButton: {
    fontFamily: designTokens.fontFamily,
    backgroundColor: "transparent",
    color: designTokens.accent[600],
    ...shorthands.border("none"),
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    lineHeight: designTokens.lineHeight.normal,
    ":hover": {
      backgroundColor: designTokens.accent[50],
    },
    ":active": {
      backgroundColor: designTokens.accent[100],
    },
    ":disabled": {
      color: designTokens.neutral[400],
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },
});

export const useSharedCardStyles = makeStyles({
  card: {
    fontFamily: designTokens.fontFamily,
    backgroundColor: designTokens.neutral.white,
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    borderRadius: designTokens.borderRadius.xl,
    padding: designTokens.spacing[6],
    boxShadow: designTokens.shadow.base,
    transition: `all ${designTokens.transition.fast}`,
  },

  cardHover: {
    ":hover": {
      boxShadow: designTokens.shadow.md,
      ...shorthands.borderColor(designTokens.neutral[300]),
    },
  },

  cardInteractive: {
    cursor: "pointer",
    ":hover": {
      boxShadow: designTokens.shadow.lg,
      ...shorthands.borderColor(designTokens.accent[200]),
      transform: "translateY(-2px)",
    },
    ":active": {
      transform: "translateY(0)",
      boxShadow: designTokens.shadow.md,
    },
  },
});

export const useSharedModalStyles = makeStyles({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    opacity: 0,
    visibility: "hidden",
    transition: `all ${designTokens.transition.fast}`,
  },

  overlayActive: {
    opacity: 1,
    visibility: "visible",
  },

  modal: {
    fontFamily: designTokens.fontFamily,
    backgroundColor: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.xl,
    boxShadow: designTokens.shadow.xl,
    maxWidth: "90%",
    maxHeight: "90%",
    display: "flex",
    flexDirection: "column",
    transform: "scale(0.95)",
    transition: `transform ${designTokens.transition.fast}`,
  },

  modalActive: {
    transform: "scale(1)",
  },

  modalHeader: {
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[6]}`,
    ...shorthands.borderBottom("1px", "solid", designTokens.neutral[200]),
  },

  modalTitle: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.lg,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    margin: 0,
  },

  modalBody: {
    padding: designTokens.spacing[6],
    ...shorthands.overflow("auto"),
    flex: 1,
  },

  modalFooter: {
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[6]}`,
    ...shorthands.borderTop("1px", "solid", designTokens.neutral[200]),
    display: "flex",
    justifyContent: "flex-end",
    ...shorthands.gap(designTokens.spacing[3]),
  },
});

export const useSharedFormStyles = makeStyles({
  field: {
    marginBottom: designTokens.spacing[4],
  },

  label: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.neutral[700],
    marginBottom: designTokens.spacing[2],
    display: "block",
  },

  input: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    borderRadius: designTokens.borderRadius.md,
    ...shorthands.border("1px", "solid", designTokens.neutral[300]),
    width: "100%",
    transition: `all ${designTokens.transition.fast}`,
    lineHeight: designTokens.lineHeight.normal,
    ":focus": {
      ...shorthands.outline("2px", "solid", designTokens.accent[500]),
      outlineOffset: "2px",
      ...shorthands.borderColor(designTokens.accent[500]),
    },
    ":hover": {
      ...shorthands.borderColor(designTokens.neutral[400]),
    },
    "::placeholder": {
      color: designTokens.neutral[400],
    },
  },

  textarea: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    borderRadius: designTokens.borderRadius.md,
    ...shorthands.border("1px", "solid", designTokens.neutral[300]),
    width: "100%",
    minHeight: "100px",
    resize: "vertical",
    transition: `all ${designTokens.transition.fast}`,
    lineHeight: designTokens.lineHeight.normal,
    ":focus": {
      ...shorthands.outline("2px", "solid", designTokens.accent[500]),
      outlineOffset: "2px",
      ...shorthands.borderColor(designTokens.accent[500]),
    },
    ":hover": {
      ...shorthands.borderColor(designTokens.neutral[400]),
    },
    "::placeholder": {
      color: designTokens.neutral[400],
    },
  },
});

export const useSharedTagStyles = makeStyles({
  tag: {
    fontFamily: designTokens.fontFamily,
    display: "inline-flex",
    alignItems: "center",
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.full,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.medium,
    backgroundColor: designTokens.neutral[100],
    color: designTokens.neutral[700],
    ...shorthands.gap(designTokens.spacing[1]),
  },

  tagAccent: {
    backgroundColor: designTokens.accent[50],
    color: designTokens.accent[700],
    ...shorthands.border("1px", "solid", designTokens.accent[200]),
  },

  tagSuccess: {
    backgroundColor: designTokens.semantic.successLight,
    color: designTokens.semantic.success,
  },

  tagError: {
    backgroundColor: designTokens.semantic.errorLight,
    color: designTokens.semantic.error,
  },

  tagWarning: {
    backgroundColor: designTokens.semantic.warningLight,
    color: designTokens.semantic.warning,
  },

  tagInfo: {
    backgroundColor: designTokens.semantic.infoLight,
    color: designTokens.semantic.info,
  },
});

export const useSharedTextStyles = makeStyles({
  heading1: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize["2xl"],
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    lineHeight: designTokens.lineHeight.tight,
    margin: 0,
  },

  heading2: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.xl,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    lineHeight: designTokens.lineHeight.tight,
    margin: 0,
  },

  heading3: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.lg,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    lineHeight: designTokens.lineHeight.tight,
    margin: 0,
  },

  bodyLarge: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.base,
    fontWeight: designTokens.fontWeight.normal,
    color: designTokens.neutral[700],
    lineHeight: designTokens.lineHeight.normal,
  },

  body: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.normal,
    color: designTokens.neutral[600],
    lineHeight: designTokens.lineHeight.normal,
  },

  caption: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.normal,
    color: designTokens.neutral[500],
    lineHeight: designTokens.lineHeight.normal,
  },

  label: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[500],
    textTransform: "uppercase",
    letterSpacing: "0.02em",
  },
});

export const useSharedSearchBoxStyles = makeStyles({
  searchBox: {
    width: "100%",
    maxWidth: "none",
    maxHeight: "32px",
    minHeight: "32px",
    ":hover": {
      ...shorthands.borderColor(designTokens.neutral[400]),
    },
    ":focus-within": {
      ...shorthands.borderColor(designTokens.accent[500]),
      boxShadow: `0 0 0 3px ${designTokens.accent[100]}`,
    },
  },
  helperText: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    marginTop: designTokens.spacing[1],
  },
});

export const useSharedPageSearchStyles = makeStyles({
  searchInput: {
    width: "100%",
    maxWidth: "100%",
    backgroundColor: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.lg,
    border: `1px solid ${designTokens.neutral[300]}`,
    "::placeholder": {
      color: designTokens.neutral[500],
    },
    "&:focus-within": {
      border: `1px solid ${designTokens.accent[500]}`,
      boxShadow: `0 0 0 1px ${designTokens.accent[500]}`,
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
    "@media (max-width: 640px)": {
      borderRadius: designTokens.borderRadius.md,
    },
  },
});

export const useSharedDropdownStyles = makeStyles({
  dropdown: {
    minWidth: "240px",
    maxHeight: "32px",
    minHeight: "32px",
  },
  listbox: {
    maxHeight: "200px !important" as any,

    overflowY: "auto !important" as any,
    "& [role='option']": {
      paddingTop: designTokens.spacing[1],
      paddingBottom: designTokens.spacing[2],
      minHeight: "28px",
      fontSize: designTokens.fontSize.sm,
    },
  },
});

export const useSharedFilterBarStyles = makeStyles({
  filterRow: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: designTokens.spacing[2],
    flexWrap: "wrap",
    width: "100%",
  },
  searchContainer: {
    display: "flex",
    flexDirection: "column",
    minWidth: "200px",
    flex: 1,
  },
});

export const useSharedSectionStyles = makeStyles({
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    marginBottom: designTokens.spacing[3],
    paddingBottom: designTokens.spacing[2],
    ...shorthands.borderBottom("1px", "solid", designTokens.neutral[200]),
  },
  sectionTitle: {
    fontFamily: designTokens.fontFamily,
    fontWeight: designTokens.fontWeight.semibold,
    fontSize: designTokens.fontSize.base,
    color: designTokens.neutral[900],
    margin: 0,
  },
  sectionCount: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    backgroundColor: designTokens.neutral[100],
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[1],
    paddingLeft: designTokens.spacing[2],
    paddingRight: designTokens.spacing[2],
    borderRadius: designTokens.borderRadius.md,
  },
  emptyState: {
    textAlign: "center",
    color: designTokens.neutral[500],
    padding: designTokens.spacing[10],
    fontSize: designTokens.fontSize.sm,
  },
});

export const useSharedBannerStyles = makeStyles({
  infoBanner: {
    paddingTop: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[2],
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[3],
    backgroundColor: designTokens.neutral[50],
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[600],
    marginBottom: designTokens.spacing[4],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  infoBannerLink: {
    color: designTokens.accent[500],
    fontWeight: designTokens.fontWeight.medium,
    textDecorationLine: "none",
    ":hover": {
      textDecorationLine: "underline",
    },
  },
  warningBanner: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    backgroundColor: designTokens.semantic.warningLight,
    border: `1px solid ${designTokens.semantic.warning}`,
    borderRadius: designTokens.borderRadius.lg,
    fontSize: designTokens.fontSize.sm,
    fontFamily: designTokens.fontFamily,
    color: designTokens.semantic.warningDark,
    flexShrink: 0,
  },
  warningBannerText: {
    flex: 1,
    lineHeight: designTokens.lineHeight.normal,
  },
  warningBannerAction: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
    backgroundColor: designTokens.neutral[800],
    color: designTokens.neutral.white,
    border: "none",
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    fontFamily: designTokens.fontFamily,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: `background-color ${designTokens.transition.fast}`,
    ":hover": {
      backgroundColor: designTokens.neutral[900],
    },
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    backgroundColor: designTokens.semantic.errorLight,
    border: `1px solid ${designTokens.semantic.error}`,
    borderRadius: designTokens.borderRadius.lg,
    fontSize: designTokens.fontSize.sm,
    fontFamily: designTokens.fontFamily,
    color: designTokens.semantic.error,
    flexShrink: 0,
  },
  errorBannerText: {
    flex: 1,
    lineHeight: designTokens.lineHeight.normal,
  },
});

export const sharedAnimations = {
  fadeIn: {
    from: { opacity: "0" },
    to: { opacity: "1" },
  },
  fadeSlideUp: {
    from: { opacity: "0", transform: "translateY(12px)" },
    to: { opacity: "1", transform: "translateY(0)" },
  },
  fadeSlideIn: {
    from: { opacity: "0", transform: "translateY(6px)" },
    to: { opacity: "1", transform: "translateY(0)" },
  },
} as const;

export const useSharedPillButtonStyles = makeStyles({
  pillButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[2],
    paddingTop: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[2],
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[3],
    backgroundColor: designTokens.neutral.white,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.full,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.normal,
    fontFamily: designTokens.fontFamily,
    color: designTokens.neutral[700],
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    whiteSpace: "nowrap",
    minHeight: "32px",
    ":hover:not(:disabled)": {
      backgroundColor: designTokens.neutral[100],
      border: `1px solid ${designTokens.neutral[300]}`,
    },
    ":active:not(:disabled)": {
      backgroundColor: designTokens.neutral[200],
    },
    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  pillButtonActive: {
    backgroundColor: designTokens.accent[50],
    border: `1px solid ${designTokens.accent[500]}`,
    color: designTokens.accent[600],
    fontWeight: designTokens.fontWeight.medium,
    ":hover:not(:disabled)": {
      backgroundColor: designTokens.accent[50],
    },
  },
});

export const useSharedPillTabContainerStyles = makeStyles({
  tabList: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
});

export const useSharedDividerStyles = makeStyles({
  horizontal: {
    height: "1px",
    backgroundColor: designTokens.neutral[200],
    marginTop: designTokens.spacing[1],
    marginBottom: designTokens.spacing[1],
  },
  vertical: {
    width: "1px",
    height: "20px",
    backgroundColor: designTokens.neutral[200],
    flexShrink: 0,
    marginLeft: designTokens.spacing[2],
    marginRight: designTokens.spacing[2],
  },
});

export const useSharedBadgeStyles = makeStyles({
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "18px",
    height: "18px",
    backgroundColor: designTokens.accent[500],
    color: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.full,
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
    paddingLeft: designTokens.spacing[1],
    paddingRight: designTokens.spacing[1],
  },
  badgeNeutral: {
    backgroundColor: designTokens.neutral[200],
    color: designTokens.neutral[700],
  },
  badgeSuccess: {
    backgroundColor: designTokens.semantic.successLight,
    color: designTokens.semantic.success,
  },
  badgeError: {
    backgroundColor: designTokens.semantic.errorLight,
    color: designTokens.semantic.error,
  },
});

export const useSharedMenuStyles = makeStyles({
  surface: {
    backgroundColor: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.lg,
    boxShadow: designTokens.shadow.lg,
    padding: `${designTokens.spacing[1]} 0`,
    border: `1px solid ${designTokens.neutral[200]}`,
    minWidth: "180px",
    overflow: "hidden",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    paddingTop: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[2],
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[3],
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: designTokens.fontSize.sm,
    fontFamily: designTokens.fontFamily,
    color: designTokens.neutral[700],
    width: "100%",
    textAlign: "left",
    transition: `background-color ${designTokens.transition.fast}`,
    ":hover": {
      backgroundColor: designTokens.neutral[50],
      color: designTokens.neutral[900],
    },
    ":active": {
      backgroundColor: designTokens.neutral[100],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "-2px",
    },
  },
  itemDanger: {
    color: designTokens.semantic.error,
    ":hover": {
      backgroundColor: designTokens.semantic.errorLight,
      color: designTokens.semantic.error,
    },
  },
  itemIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: designTokens.spacing[5],
    height: designTokens.spacing[5],
    color: designTokens.neutral[600],
  },
  itemIconDanger: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: designTokens.spacing[5],
    height: designTokens.spacing[5],
    color: designTokens.semantic.error,
  },
});

export const useSharedCloseButtonStyles = makeStyles({
  closeButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: designTokens.controlHeight.sm,
    height: designTokens.controlHeight.sm,
    minWidth: designTokens.controlHeight.sm,
    backgroundColor: "transparent",
    border: "none",
    borderRadius: designTokens.borderRadius.md,
    cursor: "pointer",
    color: designTokens.neutral[500],
    transition: `all ${designTokens.transition.fast}`,
    flexShrink: 0,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
      color: designTokens.neutral[700],
    },
    ":active": {
      backgroundColor: designTokens.neutral[200],
    },
  },
});

export const useSharedEmptyStateStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[3],
    paddingTop: designTokens.spacing[12],
    paddingBottom: designTokens.spacing[12],
    paddingLeft: designTokens.spacing[6],
    paddingRight: designTokens.spacing[6],
    color: designTokens.neutral[500],
    textAlign: "center",
    width: "100%",
  },
  icon: {
    width: "40px",
    height: "40px",
    color: designTokens.neutral[300],
  },
  title: {
    fontSize: designTokens.fontSize.base,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[600],
    margin: 0,
  },
  description: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[500],
    margin: 0,
    maxWidth: "360px",
  },
});

export const useSharedDialogFooterStyles = makeStyles({
  dialogSurface: {
    paddingBottom: "0",
  },
  dialogSpinner: {
    paddingTop: designTokens.spacing[6],
    paddingBottom: designTokens.spacing[6],
  },
  dialogFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    ...shorthands.gap(designTokens.spacing[2]),
    marginTop: designTokens.spacing[4],
    paddingTop: designTokens.spacing[4],
    paddingBottom: designTokens.spacing[4],
    paddingLeft: designTokens.spacing[6],
    paddingRight: designTokens.spacing[6],
    marginLeft: `-${designTokens.spacing[6]}`,
    marginRight: `-${designTokens.spacing[6]}`,
    ...shorthands.borderTop("1px", "solid", designTokens.neutral[200]),
    backgroundColor: designTokens.neutral[50],
    borderBottomLeftRadius: "inherit",
    borderBottomRightRadius: "inherit",
  },
  buttonDimmed: {
    color: designTokens.neutral[400],
    borderTopColor: designTokens.neutral[300],
    borderRightColor: designTokens.neutral[300],
    borderBottomColor: designTokens.neutral[300],
    borderLeftColor: designTokens.neutral[300],
    backgroundColor: designTokens.neutral[100],
    ":hover:not(:disabled)": {
      color: designTokens.neutral[400],
      borderTopColor: designTokens.neutral[300],
      borderRightColor: designTokens.neutral[300],
      borderBottomColor: designTokens.neutral[300],
      borderLeftColor: designTokens.neutral[300],
      backgroundColor: designTokens.neutral[100],
    },
  },
  dialogFooterSpaceBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: designTokens.spacing[4],
    paddingBottom: designTokens.spacing[4],
    paddingLeft: designTokens.spacing[6],
    paddingRight: designTokens.spacing[6],
    marginLeft: `-${designTokens.spacing[6]}`,
    marginRight: `-${designTokens.spacing[6]}`,
    ...shorthands.borderTop("1px", "solid", designTokens.neutral[200]),
    backgroundColor: designTokens.neutral[50],
    borderBottomLeftRadius: "inherit",
    borderBottomRightRadius: "inherit",
  },
  dialogErrorMessage: {
    color: designTokens.semantic.error,
    padding: designTokens.spacing[2],
    backgroundColor: designTokens.semantic.errorLight,
    borderRadius: designTokens.borderRadius.sm,
  },
});

export const useSharedCircleButtonStyles = makeStyles({
  circleButton: {
    width: designTokens.spacing[10],
    height: designTokens.spacing[10],
    minWidth: designTokens.spacing[10],
    borderRadius: designTokens.borderRadius.full,
    paddingTop: "0",
    paddingRight: "0",
    paddingBottom: "0",
    paddingLeft: "0",
    color: designTokens.accent[700],
    borderTopStyle: "solid",
    borderRightStyle: "solid",
    borderBottomStyle: "solid",
    borderLeftStyle: "solid",
    borderTopWidth: "2px",
    borderRightWidth: "2px",
    borderBottomWidth: "2px",
    borderLeftWidth: "2px",
    ...shorthands.borderColor(designTokens.accent[700]),
    backgroundColor: designTokens.accent[50],
    fontSize: designTokens.fontSize.xl,
    ":hover:not(:disabled)": {
      color: designTokens.accent[600],
      borderTopColor: designTokens.accent[600],
      borderRightColor: designTokens.accent[600],
      borderBottomColor: designTokens.accent[600],
      borderLeftColor: designTokens.accent[600],
      backgroundColor: designTokens.accent[100],
    },
    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
});

export const useSharedToggleStyles = makeStyles({
  toggleSection: {
    display: "flex",
    flexDirection: "column" as const,
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
  },
  toggleHint: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    marginTop: designTokens.spacing[2],
  },
});

export const useSharedDialogTabStyles = makeStyles({
  stickyWrapper: {
    backgroundColor: designTokens.neutral.white,
    paddingTop: designTokens.spacing[3],
    boxShadow: `0 1px 0 0 ${designTokens.neutral[200]}`,
  },
  tabList: {
    display: "flex",
    gap: designTokens.spacing[1],
    ...shorthands.borderBottom("1px", "solid", designTokens.neutral[200]),
  },
  tab: {
    paddingTop: designTokens.spacing[3],
    paddingBottom: designTokens.spacing[2],
    paddingLeft: designTokens.spacing[2],
    paddingRight: designTokens.spacing[2],
    backgroundColor: "transparent",
    borderTopStyle: "none" as const,
    borderLeftStyle: "none" as const,
    borderRightStyle: "none" as const,
    borderBottomWidth: "2px",
    borderBottomStyle: "solid" as const,
    borderBottomColor: "transparent",
    marginBottom: "-1px",
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    fontFamily: designTokens.fontFamily,
    color: designTokens.neutral[500],
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    ":hover": {
      color: designTokens.neutral[700],
    },
    "&::after": {
      width: "100%",
      left: 0,
      right: 0,
    },
  },
  tabActive: {
    color: designTokens.accent[600],
    borderBottomColor: designTokens.accent[500],
  },
});

export const useSharedAdminCellStyles = makeStyles({
  nameText: {
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.neutral[900],
  },
  truncatedText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

export const useSharedAdminContentHeaderStyles = makeStyles({
  root: {
    backgroundColor: designTokens.neutral.white,
    ...shorthands.borderBottom("1px", "solid", designTokens.neutral[200]),
    paddingTop: designTokens.spacing[5],
    paddingBottom: designTokens.spacing[5],
    paddingLeft: designTokens.spacing[6],
    paddingRight: designTokens.spacing[6],
    boxSizing: "border-box",
    flexShrink: 0,
  },

  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap(designTokens.spacing[4]),
    minHeight: "36px",
  },

  titleRowWithFilters: {
    marginBottom: designTokens.spacing[5],
  },

  title: {
    fontSize: designTokens.fontSize.xl,
    fontWeight: designTokens.fontWeight.semibold,
    fontFamily: designTokens.fontFamily,
    color: designTokens.neutral[900],
    lineHeight: designTokens.lineHeight.normal,
    margin: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },

  actions: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(designTokens.spacing[2]),
    flexShrink: 0,
  },

  actionButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.gap(designTokens.spacing[2]),
    height: "36px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "& svg": {
      width: designTokens.iconSize.sm,
      height: designTokens.iconSize.sm,
      flexShrink: 0,
    },
  },

  filterRow: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(designTokens.spacing[3]),
    flexWrap: "nowrap",
  },

  searchInputWrapper: {
    position: "relative",
    flexGrow: 1,
    flexShrink: 1,
    maxWidth: "320px",
    minWidth: "160px",
  },

  searchIcon: {
    position: "absolute",
    left: designTokens.spacing[3],
    top: "50%",
    transform: "translateY(-50%)",
    width: designTokens.iconSize.sm,
    height: designTokens.iconSize.sm,
    color: designTokens.neutral[400],
    pointerEvents: "none",
  },

  searchInput: {
    width: "100%",
    height: "36px",
    boxSizing: "border-box",
    backgroundColor: designTokens.neutral[50],
    ...shorthands.border("1px", "solid", designTokens.neutral[300]),
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    paddingTop: "0",
    paddingBottom: "0",
    paddingLeft: "40px",
    paddingRight: designTokens.spacing[3],
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[900],
    ...shorthands.outline("none"),
    transition: `border-color ${designTokens.transition.fast}, box-shadow ${designTokens.transition.fast}`,
    "::placeholder": {
      color: designTokens.neutral[400],
    },
    ":focus": {
      ...shorthands.borderColor(designTokens.accent[500]),
      boxShadow: `0 0 0 3px ${designTokens.accent[100]}`,
      backgroundColor: designTokens.neutral.white,
    },
  },

  filterSelectWrapper: {
    position: "relative",
    flexShrink: 0,
  },

  filterSelect: {
    height: "36px",
    minWidth: "140px",
    boxSizing: "border-box",
    backgroundColor: designTokens.neutral.white,
    ...shorthands.border("1px", "solid", designTokens.neutral[300]),
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    paddingTop: "0",
    paddingBottom: "0",
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[8],
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[700],
    ...shorthands.outline("none"),
    cursor: "pointer",
    transition: `border-color ${designTokens.transition.fast}`,
    appearance: "none",
    ":focus": {
      ...shorthands.borderColor(designTokens.accent[500]),
    },
  },

  filterChevron: {
    position: "absolute",
    right: designTokens.spacing[2],
    top: "50%",
    transform: "translateY(-50%)",
    width: designTokens.iconSize.sm,
    height: designTokens.iconSize.sm,
    color: designTokens.neutral[500],
    pointerEvents: "none",
  },
});

export const useSharedPageContainerStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    boxSizing: "border-box",
    padding: `${designTokens.spacing[8]} ${designTokens.spacing[6]}`,
    "@media (min-width: 768px)": {
      padding: `${designTokens.spacing[10]} ${designTokens.spacing[24]}`,
    },
    "@media (min-width: 1200px)": {
      padding: `${designTokens.spacing[12]} ${designTokens.spacing[32]}`,
    },
    gap: designTokens.spacing[5],
    width: "100%",
    overflow: "hidden",
    backgroundColor: designTokens.neutral[50],
    "@media (max-width: 640px)": {
      padding: `${designTokens.spacing[6]} 0`,
      gap: designTokens.spacing[4],
    },
  },
});

export const useSharedPageHeaderStyles = makeStyles({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: designTokens.spacing[4],
    overflowY: "hidden",
    scrollbarGutter: "stable",
    paddingLeft: designTokens.spacing[6],
    paddingRight: designTokens.spacing[6],
    "@media (min-width: 768px)": {
      paddingLeft: designTokens.spacing[24],
      paddingRight: designTokens.spacing[24],
    },
    "@media (min-width: 1200px)": {
      paddingLeft: designTokens.spacing[32],
      paddingRight: designTokens.spacing[32],
    },
    "@media (max-width: 640px)": {
      paddingLeft: designTokens.spacing[4],
      paddingRight: designTokens.spacing[4],
    },
    "@media (max-width: 480px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: designTokens.spacing[2],
    },
  },
  title: {
    fontSize: designTokens.fontSize["2xl"],
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    margin: 0,
    "@media (max-width: 640px)": {
      display: "none",
    },
  },
  createButton: {
    backgroundColor: designTokens.neutral[800],
    color: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.md,
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
    border: "none",
    ":hover": {
      backgroundColor: designTokens.neutral[700],
      color: designTokens.neutral.white,
    },
    "@media (max-width: 640px)": {
      fontSize: designTokens.fontSize.sm,
      padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    },
  },
});

export const useSharedResponsiveSectionStyles = makeStyles({
  root: {
    overflowY: "hidden",
    scrollbarGutter: "stable",
    paddingLeft: designTokens.spacing[6],
    paddingRight: designTokens.spacing[6],
    "@media (min-width: 768px)": {
      paddingLeft: designTokens.spacing[24],
      paddingRight: designTokens.spacing[24],
    },
    "@media (min-width: 1200px)": {
      paddingLeft: designTokens.spacing[32],
      paddingRight: designTokens.spacing[32],
    },
    "@media (max-width: 640px)": {
      paddingLeft: designTokens.spacing[4],
      paddingRight: designTokens.spacing[4],
    },
  },
});

export const useSharedSortControlStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: designTokens.spacing[3],
    overflowY: "hidden",
    scrollbarGutter: "stable",
    paddingLeft: designTokens.spacing[6],
    paddingRight: designTokens.spacing[6],
    "@media (min-width: 768px)": {
      paddingLeft: designTokens.spacing[24],
      paddingRight: designTokens.spacing[24],
    },
    "@media (min-width: 1200px)": {
      paddingLeft: designTokens.spacing[32],
      paddingRight: designTokens.spacing[32],
    },
    "@media (max-width: 480px)": {
      paddingLeft: designTokens.spacing[4],
      paddingRight: designTokens.spacing[4],
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  sortGroup: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
  sortLabel: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[600],
    fontWeight: designTokens.fontWeight.medium,
    whiteSpace: "nowrap",
  },
  sortMenuButton: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.neutral[600],
    whiteSpace: "nowrap",
    backgroundColor: designTokens.neutral.white,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.md,
    paddingRight: designTokens.spacing[3],
    transition: `all ${designTokens.transition.fast}`,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
      border: `1px solid ${designTokens.neutral[300]}`,
    },
  },
  sortDirectionButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: designTokens.neutral.white,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.md,
    cursor: "pointer",
    padding: designTokens.spacing[1],
    color: designTokens.neutral[600],
    minWidth: designTokens.controlHeight.sm,
    minHeight: designTokens.controlHeight.sm,
    transition: `all ${designTokens.transition.fast}`,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
      border: `1px solid ${designTokens.neutral[300]}`,
      color: designTokens.neutral[800],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
});

export const useSharedCardGridStyles = makeStyles({
  gridScroll: {
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarGutter: "stable",
    minHeight: 0,
    flex: 1,
    marginRight: designTokens.spacing[6],
    "@media (min-width: 768px)": {
      marginRight: designTokens.spacing[24],
    },
    "@media (min-width: 1200px)": {
      marginRight: designTokens.spacing[32],
    },
    "@media (max-width: 640px)": {
      marginRight: designTokens.spacing[4],
    },
    "@media (max-width: 480px)": {
      marginRight: designTokens.spacing[4],
    },
  },
  gridContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    overflow: "hidden",
    paddingTop: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[1],
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: designTokens.spacing[4],
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    paddingLeft: designTokens.spacing[6],
    paddingRight: designTokens.spacing[3],
    "@media (min-width: 768px)": {
      paddingLeft: designTokens.spacing[24],
    },
    "@media (min-width: 1200px)": {
      paddingLeft: designTokens.spacing[32],
    },
    "@media (max-width: 640px)": {
      paddingLeft: designTokens.spacing[4],
      gap: designTokens.spacing[3],
    },
    "@media (max-width: 480px)": {
      gridTemplateColumns: "1fr",
      paddingLeft: designTokens.spacing[4],
      gap: designTokens.spacing[3],
    },
  },
});

export const useSharedPageEmptyStateStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: "400px",
    ...shorthands.gap(designTokens.spacing[4]),
    padding: designTokens.spacing[8],
    "@media (max-width: 640px)": {
      padding: designTokens.spacing[6],
      minHeight: "300px",
      ...shorthands.gap(designTokens.spacing[3]),
    },
  },
  icon: {
    fontSize: "64px",
    color: designTokens.neutral[300],
    "@media (max-width: 640px)": {
      fontSize: "48px",
    },
  },
  title: {
    fontSize: designTokens.fontSize.lg,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    margin: 0,
    "@media (max-width: 640px)": {
      fontSize: designTokens.fontSize.base,
      textAlign: "center",
    },
  },
  subtitle: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[600],
    margin: 0,
    "@media (max-width: 640px)": {
      fontSize: designTokens.fontSize.xs,
      textAlign: "center",
    },
  },
});

export const useSharedCardDetailStyles = makeStyles({
  cardOverride: {
    padding: designTokens.spacing[5],
    gap: designTokens.spacing[3],
    display: "flex",
    flexDirection: "column",
    position: "relative",
    minWidth: 0,
    overflow: "hidden",
    ":hover": {
      boxShadow: designTokens.shadow.md,
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
    "@media (max-width: 480px)": {
      padding: designTokens.spacing[4],
      gap: designTokens.spacing[2],
    },
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: designTokens.spacing[2],
    flexShrink: 0,
  },

  titleSection: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    flex: 1,
    minWidth: 0,
  },

  fixedTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    flex: 1,
    minWidth: 0,
    height: designTokens.card.fixedTitleSectionHeight,
  },

  title: {
    fontWeight: designTokens.fontWeight.bold,
    fontSize: designTokens.fontSize.base,
    color: designTokens.neutral[900],
    lineHeight: designTokens.lineHeight.tight,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    textOverflow: "ellipsis",
    wordBreak: "break-word" as const,
    minHeight: designTokens.spacing[10],
  },

  subtitle: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[500],
    fontFamily: designTokens.fontFamily,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },

  description: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[600],
    fontFamily: designTokens.fontFamily,
    lineHeight: designTokens.lineHeight.normal,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    textOverflow: "ellipsis",
    wordBreak: "break-word" as const,
  },

  fixedDescription: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[600],
    fontFamily: designTokens.fontFamily,
    lineHeight: designTokens.lineHeight.normal,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    textOverflow: "ellipsis",
    wordBreak: "break-word" as const,
    minHeight: designTokens.card.fixedDescriptionMinHeight,
  },

  emptyValue: {
    color: designTokens.neutral[500],
  },

  metadataSection: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    marginTop: "auto",
  },

  metadataRow: {
    display: "flex",
    alignItems: "baseline",
    gap: designTokens.spacing[2],
    fontSize: designTokens.fontSize.sm,
    fontFamily: designTokens.fontFamily,
  },

  metadataLabel: {
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[700],
    flexShrink: 0,
  },

  metadataValue: {
    color: designTokens.neutral[600],
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },

  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: designTokens.spacing[3],
    paddingTop: designTokens.spacing[3],
    borderTop: `1px solid ${designTokens.neutral[100]}`,
    marginTop: "auto",
  },
});

export const useSharedDialogContentStyles = makeStyles({
  dialogSurface: {
    paddingBottom: "0",
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
    minHeight: "480px",
  },
  tabList: {
    marginLeft: `calc(-1 * ${designTokens.spacing[3]})`,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
    position: "relative",
  },
  tabbedDialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },
  tabContent: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
    flex: 1,
    minHeight: 0,
    position: "relative",
  },
  scrollableContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto" as const,
  },
  formLoadingOverlay: {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    backgroundColor: `color-mix(in srgb, ${designTokens.neutral.white} 80%, transparent)`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    borderRadius: designTokens.borderRadius.sm,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "480px",
  },
  feedbackError: {
    color: designTokens.semantic.error,
    marginTop: designTokens.spacing[2],
  },
  feedbackErrorBox: {
    color: designTokens.semantic.error,
    padding: designTokens.spacing[2],
    backgroundColor: designTokens.semantic.errorLight,
    borderRadius: designTokens.borderRadius.sm,
  },
  formHelpText: {
    marginTop: designTokens.spacing[1],
    opacity: 0.7,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: designTokens.spacing[2],
  },
});

export const useSharedFileCardStyles = makeStyles({
  fileList: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },
  fileCard: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    borderRadius: designTokens.borderRadius.md,
    border: `1px solid ${designTokens.accent[200]}`,
    backgroundColor: designTokens.accent[50],
  },
  fileIcon: {
    color: designTokens.neutral[500],
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },
  fileInfo: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },
  fileName: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.neutral[900],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  },
  fileStatus: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.accent[600],
    display: "block",
  },
  fileActions: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    flexShrink: 0,
    marginLeft: "auto",
  },
  fileActionButton: {
    minWidth: "auto",
    color: designTokens.neutral[400],
    ":hover": {
      color: designTokens.neutral[700],
    },
  },
  fileDeleteButton: {
    minWidth: "auto",
    color: designTokens.neutral[400],
    ":hover": {
      color: designTokens.neutral[700],
    },
  },
  fileDownloadButton: {
    minWidth: "auto",
    color: designTokens.neutral[400],
    ":hover": {
      color: designTokens.accent[600],
    },
  },
});

export { designTokens };
